import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate';
import { Event } from '@adornis/buildify/client/globals/enums';
import { XBuildify } from '@adornis/buildify/client/x-buildify';
import type { Base } from '@adornis/buildify/db/Base';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/stack/x-stack.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { XDialog } from '@adornis/dialog/x-dialog';
import { stateful } from '@adornis/functional-lit/stateful';
import '@adornis/html-based-buildify/client/editor/x-editor';
import { html, nothing, render, type TemplateResult } from 'lit';
import type { DirectiveResult } from 'lit/async-directive';
import { customElement, property } from 'lit/decorators.js';
import { changeDraftStatus } from '../../drafts/api/changeDraftStatus';
import { saveRawDraft } from '../../drafts/api/saveRawDraft';
import { PublishDraftDialog } from '../../drafts/client/PublishDraftDialog';
import type { Draft } from '../../drafts/db/Draft';
import { getCommentByReferenceAndContentID } from '../api/getCommentByReferenceAndContentID';
import { ContentComment } from '../db/ContentComment';
import { AddCommentDialog } from './AddCommentDialog';
import { ViewCommentComponent } from './ViewComment';
import { ViewCommentsByReference } from './ViewCommentsByReference';

export const CommentOnBuildify = stateful(
  ({
    buildifyContent,
    relatedEntityID,
    content,
    draft,
    onNavigateBackCallback,
    globalSettingsClassName,
  }: {
    buildifyContent: TemplateResult | DirectiveResult;
    relatedEntityID: string;
    content: Base[];
    draft: Draft;
    onNavigateBackCallback: () => void;
    globalSettingsClassName: string;
  }) => {
    return html`
      <comment-on-buildify
        .relatedEntityID=${relatedEntityID}
        .content=${content}
        .draft=${draft}
        .onNavigateBackCallback=${onNavigateBackCallback}
        .globalSettingsClassName=${globalSettingsClassName}
      >
        ${buildifyContent}
      </comment-on-buildify>
    `;
  },
);

@customElement('comment-on-buildify')
export class CommentOnBuildifyComponent extends ChemistryLitElement {
  @property({ attribute: false }) relatedEntityID: string | undefined;
  @property({ attribute: false }) content: Base[] | undefined;
  @property({ attribute: false }) draft: Draft | undefined;
  @property({ attribute: false }) globalSettingsClassName: string | undefined;
  @property({ attribute: false }) onNavigateBackCallback: (() => void) | undefined;

  private _hoveredElement: ExtensionBoilerplate<any> | undefined;
  private _appendedElement: HTMLElement | undefined;

  private _commentedElement: ExtensionBoilerplate<any> | undefined;

  override connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener('mouseleave', e => {
      if (this._commentedElement || this._isEditMode()) return;

      this._removeHoveredStylings();
    });
    this.addEventListener('mousemove', event => {
      if (this._commentedElement || this._isEditMode()) return;

      const path = event.composedPath();
      const buildifyElement = path.find(el => {
        if (el instanceof ExtensionBoilerplate && !el.hasAttribute('sub')) return true;
        return false;
      }) as ExtensionBoilerplate<any> | undefined;

      if (this._hoveredElement === buildifyElement) return;

      if (this._hoveredElement && this._hoveredElement !== buildifyElement) this._removeHoveredStylings();

      this._hoveredElement = buildifyElement;
      if (this._hoveredElement) this._addHoveredStylings(this._hoveredElement);
    });
  }

  private _addHoveredStylings(element: HTMLElement) {
    for (const [key, value] of Object.entries(this.getHoveredStylings())) {
      element.style[key] = value;
    }

    const child = document.createElement('div') as HTMLDivElement;
    child.style.position = 'absolute';
    child.style.background = 'rgba(0,0,0,.5)';
    child.style.inset = '0';
    child.style.cursor = 'pointer';
    child.style.zIndex = '9999';
    child.style.pointerEvents = 'none';

    const icon = document.createElement('d-icon');
    icon.innerText = 'message-check';
    icon.setAttribute('solid', '');
    icon.style.position = 'absolute';
    icon.style.right = '0';
    icon.style.top = '0';
    icon.style.padding = '2px';
    icon.style.fontSize = '14px';
    icon.style.background = '#fff';
    icon.style.color = '#333';
    icon.style.pointerEvents = 'all';
    icon.style.cursor = 'pointer';

    icon.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      const contentID = this._hoveredElement?.content.value._id;
      if (!contentID || !this.relatedEntityID || !this.globalSettingsClassName) return;

      AddCommentDialog({
        relatedEntityID: this.relatedEntityID,
        contentID,
        globalSettingsClassName: this.globalSettingsClassName,
      });
    });
    child.appendChild(icon);

    const shadow = element.shadowRoot;
    shadow?.appendChild(child);
    this._appendedElement = child;
  }

  private _removeHoveredStylings() {
    for (const key of Object.keys(this.getHoveredStylings())) {
      if (!this._hoveredElement) break;
      this._hoveredElement.style[key] = '';
    }
    this._appendedElement?.remove();
  }

  private getHoveredStylings(): Styles {
    return {
      boxShadow: '0 0 10px 4px rgba(0, 150, 255, 0.7)',
      position: 'relative',
    };
  }

  override render() {
    const draft = this.draft;
    if (!this.relatedEntityID || !draft) return nothing;

    return html`
      <x-grid columns="1fr 350px" space="md" ${css({ maxWidth: '100%' })}>
        <div style="max-width: 100%; overflow: hidden;">
          <slot></slot>
        </div>
        <div ${css({ position: 'sticky', top: '0px', height: 'max-content' })}>
          <d-button
            ${css({ width: '100%', marginBottom: '24px' })}
            @click=${async () => {
              await saveRawDraft(draft)();

              if (draft.statusKey === 'IN_REVISION') {
                await PublishDraftDialog(draft);
                this.onNavigateBackCallback?.();
              }

              if (draft.statusKey === 'IN_REVIEW') {
                if (!(await XDialog.confirm('Willst du die Korrektur wirklich abschließen?'))) return;
                await changeDraftStatus(draft._id, 'IN_REVISION')();
                XSnackbar.show('Korrektur abgeschlossen!');
                this.onNavigateBackCallback?.();
              }
            }}
          >
            ${draft.statusKey === 'IN_REVISION'
              ? html` Jetzt veröffentlichen <d-icon solid> upload </d-icon> `
              : html` Korrektur abschließen <d-icon solid> check-double </d-icon> `}
          </d-button>
          ${ViewCommentsByReference({
            globalSettingsClassName: this.globalSettingsClassName!,
            referenceID: this.relatedEntityID,
            onHover: comment => {
              if (!(comment instanceof ContentComment) || !this.content || this._commentedElement) return;

              let pathIDs: string[] = [];
              const setParentRecursively = (contentID: string) => {
                pathIDs.push(contentID);
                const content = this.content!.find(c => c._id === contentID);
                if (content?.parentID) setParentRecursively(content.parentID);
              };
              setParentRecursively(comment.contentID);
              pathIDs = pathIDs.reverse();

              const buildifyElement = this._getXBuildify();
              if (!buildifyElement) return;

              let element = buildifyElement;
              for (const id of pathIDs) {
                element = findBoilerplateById(element, id);
              }
              if (!element || !(element instanceof ExtensionBoilerplate)) return;
              this._removeHoveredStylings();

              const overlay = document.createElement('div');
              overlay.style.background = 'orange';
              overlay.style.opacity = '.5';
              overlay.style.position = 'absolute';
              overlay.style.inset = '0';
              overlay.style.zIndex = '999999';

              element.style.position = 'relative';
              element.shadowRoot?.appendChild(overlay);

              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
              this._commentedElement = element;

              setTimeout(() => {
                if (!this._commentedElement) return;
                overlay.remove();
                this._commentedElement = undefined;
              }, 2000);
            },
          })}
        </div>
      </x-grid>
    `;
  }

  private _xbuildifyElement: XBuildify | undefined;
  private _visualizeCommentContainer: HTMLDivElement | undefined;
  private _getXBuildify() {
    if (this._xbuildifyElement) return this._xbuildifyElement;
    const assignedElements = this.renderRoot.querySelector('slot')?.assignedElements();
    if (!assignedElements) return null;
    const children = assignedElements.map(element => Array.from(element.children)).flat();
    const buildifyElement = children.find(element => element instanceof XBuildify);
    if (!buildifyElement) return null;

    this._xbuildifyElement = buildifyElement;
    buildifyElement.addEventListener(Event.SET_EDIT_CONTENT, async (e: ValueEvent<Maybe<Base>>) => {
      const contentID = e.detail.value?._id;
      const drawerPanel = findDrawerPanel(this._xbuildifyElement);

      if (!contentID || !drawerPanel) {
        this._visualizeCommentContainer?.remove();
        return;
      }

      const comment = await getCommentByReferenceAndContentID(
        this.relatedEntityID ?? '',
        contentID,
      )(ContentComment.allFields);
      if (!comment) return;

      // Erstelle ein Container-Element für den Kommentar
      const commentContainer = document.createElement('div');
      commentContainer.style.padding = '16px';

      // Füge den Container als erstes Kind im DrawerPanel ein
      if (drawerPanel.firstChild) {
        drawerPanel.insertBefore(commentContainer, drawerPanel.firstChild);
      } else {
        drawerPanel.appendChild(commentContainer);
      }

      // Rendere den Kommentar in den Container
      render(
        ViewCommentComponent.render({
          comment,
          hideActions: true,
          globalSettingsClassName: this.globalSettingsClassName!,
        }),
        commentContainer,
      );
      this._visualizeCommentContainer = commentContainer;
    });

    return buildifyElement;
  }

  private _isEditMode() {
    const editor = this._getXBuildify();
    return editor?.isEditor.value ?? false;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'block',
          position: 'relative',
        },
      },
    ] as Styles[];
  }
}

function findBoilerplateById(element, targetId) {
  if (!element) return null;

  // Prüfe, ob das aktuelle Element eine Boilerplate-Instanz mit der gesuchten ID ist
  if (element instanceof ExtensionBoilerplate && element.content.value._id === targetId) {
    return element;
  }

  // Falls das Element ein Shadow Root hat, dort auch suchen
  if (element.shadowRoot) {
    // 1. Suche in Shadow Root's direkten Kindern
    for (const child of element.shadowRoot.children) {
      const found = findBoilerplateById(child, targetId);
      if (found) return found;
    }

    // 2. Falls es Slots gibt, prüfe die assignedElements()
    const slots = element.shadowRoot.querySelectorAll('slot');
    for (const slot of slots) {
      for (const assigned of slot.assignedElements()) {
        const found = findBoilerplateById(assigned, targetId);
        if (found) return found;
      }
    }
  }

  // Durchlaufe alle normalen Kinder (Light DOM)
  for (const child of element.children) {
    const found = findBoilerplateById(child, targetId);
    if (found) return found;
  }

  return null; // Falls kein passendes Element gefunden wurde
}

/**
 * Findet das XDrawerPanel innerhalb eines XBuildify-Elements
 * @param xbuildifyElement Das XBuildify-Element, in dem gesucht werden soll
 * @returns Das gefundene XDrawerPanel oder null, wenn keines gefunden wurde
 */
function findDrawerPanel(xbuildifyElement): HTMLElement | null {
  if (!xbuildifyElement) return null;

  // Hilfsfunktion zum Prüfen, ob ein DrawerPanel gültig ist (kein d-buildify-global-settings enthält)
  const isValidDrawerPanel = (panel: HTMLElement): boolean => {
    return !panel.querySelector('d-buildify-global-settings');
  };

  // Suche im Shadow DOM des XBuildify-Elements
  if (xbuildifyElement.shadowRoot) {
    // Direkte Suche nach dem drawer-panel
    const drawerPanels = xbuildifyElement.shadowRoot.querySelectorAll('d-drawer-panel');
    for (const panel of drawerPanels) {
      if (isValidDrawerPanel(panel as HTMLElement)) {
        return panel as HTMLElement;
      }
    }

    // Rekursive Suche in allen Kindern des Shadow DOM
    for (const child of xbuildifyElement.shadowRoot.children) {
      // Prüfe, ob das Kind selbst ein Shadow DOM hat
      if (child.shadowRoot) {
        const nestedDrawerPanels = child.shadowRoot.querySelectorAll('d-drawer-panel');
        for (const panel of nestedDrawerPanels) {
          if (isValidDrawerPanel(panel as HTMLElement)) {
            return panel as HTMLElement;
          }
        }

        // Prüfe auch tiefer verschachtelte Shadow DOMs
        for (const nestedChild of child.shadowRoot.children) {
          if (nestedChild.shadowRoot) {
            const deepNestedDrawerPanels = nestedChild.shadowRoot.querySelectorAll('d-drawer-panel');
            for (const panel of deepNestedDrawerPanels) {
              if (isValidDrawerPanel(panel as HTMLElement)) {
                return panel as HTMLElement;
              }
            }
          }
        }
      }
    }
  }

  return null;
}
